import { handleSocialGifting, isExpired, waitForAnonymousStart } from "./auth";
import { getFingerprint } from "../../public/lib/helpers/fingerprint";
import { AuthFlow } from "./AbstractAuthFlow";
import { get } from "svelte/store";
import { getAllSearchParams, getSearchParam } from "../document";
import * as campaignApi from "../../api/campaign";
import * as authApi from "../../api/auth";
import { updateSession, sessionStore } from "../../stores/session";
import { campaignStore } from "../../stores/campaign";
import type { Campaign } from "../../../types/campaign";
import pickBy from "lodash/pickBy";
import identity from "lodash/identity";
import type { AnonymousAuthData } from "../../api/campaign";
import { HttpError } from "../../public/lib/api/utils";
import type { ApiResponse } from "../../../types/api";
import { navigateToPath } from "../../routing";
import PATHS from "../../constants/routePaths";
import { trackSiteVisit } from "../../apiLoader/track";
import { isTestMode } from "../../stores/mode";
import { buildAnonymousFoojiData } from "../../public/lib/helpers/authData";
import { settingsStore } from "../../stores/settings";
import { withRetry } from "../../utils";

export const AnonymousAuthErrorResponseHandler = {
  handleErrorResponse: async (error: unknown) => {
    if (error instanceof HttpError) {
      const missingChecksumRegex = new RegExp("Missing fingerprint_checksum which is required for workflow", "g");
      const badChecksumRegex = new RegExp("Fingerprint checksum '.*' calculated is invalid for workflow");

      try {
        const { error: responseError } = (await error.response.json()) as ApiResponse;

        if (responseError) {
          const isMissingChecksum = missingChecksumRegex.test(responseError);
          const isBadChecksum = badChecksumRegex.test(responseError);

          if (isMissingChecksum || isBadChecksum) {
            return navigateToPath(PATHS.RESTRICTED_CAMPAIGN_INFO);
          }
        }
      } catch (jsonError) {
        // Ignore the error, let it go through to the end of the function
      }
    }

    throw error;
  }
};

export class AnonymousAuthFlow implements AuthFlow {
  async anonymousAuth({ processKey, fingerprint }: { processKey: string; fingerprint: string; }) {
    const { campaign } = get(campaignStore) as { campaign: Campaign };

    const channel = getSearchParam("c") === "insta" ? "instagram-link" : "anonymous";
    const foojiData = buildAnonymousFoojiData({ campaign, processKey, fingerprint, channel });

    const { jwt }: { jwt: string } = await authApi.authAnonymous({
      fingerprint: fingerprint,
      fingerprint_description: window.navigator.userAgent,
      fooji_data: foojiData,
      language: get(sessionStore).language,
      client: "microsite",
      turnstile_token: get(sessionStore).turnstileTokenData?.token
    });

    updateSession({ foojiData, jwt });
    trackSiteVisit();
    await waitForAnonymousStart();
    updateSession({ authCompleted: true });
  }

  async testStart() {
    if (isExpired({ requiresExpiry: true })) {
      return navigateToPath(PATHS.LINK_EXPIRED);
    }

    const {
      pk: processKey,
      tf: testFingerprint
    } = getAllSearchParams();

    if (processKey && testFingerprint) {
      await this.anonymousAuth({ processKey, fingerprint: testFingerprint });
    } else {
      console.log("Test link is missing fingerprint and/or process key:", get(sessionStore).searchParams);
      navigateToPath(PATHS.ERROR);
    }
  }

  buildAnonumousAuthData() {
    const { campaign } = get(campaignStore) as { campaign: Campaign };

    const {
      f: fingerprint,
      c,
      e: expiry,
      i: itemKey,
      v: variation,
      s: socialAccount,
      ssm: skipStockManagement,
      sg: socialGifting,
      ct: count,
      t: type,
      ds: deliveryService,
      col: collection
    } = getAllSearchParams();

    // FIXME: Can't we really migrate this to use a JWT instead?
    // FIXME: Both channel and checksum use "c"
    // To avoid setting both, just checked if "f" is set
    // but, if we ever need to support both, we will need to change this
    const channelOrChecksum = fingerprint
      ? { fingerprint_checksum: c  }
      : { channel: c }; // TODO: Should be anonymous, can't support unique link + instagram-link. Is this needed?

    const anonymousAuthData: campaignApi.AnonymousAuthData = {
      fingerprint: fingerprint ?? getFingerprint(),
      ...channelOrChecksum,
      client: "microsite",
      workflow_key: campaign?.campaign_key,
      expiry,
      item_key: itemKey,
      variation,
      social_account: socialAccount,
      skip_stock_management: skipStockManagement === "true",
      social_gifting: socialGifting === "true",
      collection,
      count: Number.isNaN(Number(count)) ? 1 : Number(count) ?? 1,
      type,
      delivery_service: deliveryService
    };

    return pickBy(anonymousAuthData, identity) as AnonymousAuthData;
  }

  async start() {
    if (isTestMode()) {
      return this.testStart();
    }

    const turnstileTokenData = get(sessionStore).turnstileTokenData;
    if (!turnstileTokenData && get(settingsStore).requiresCaptcha) {
      console.error("No turnstile token found in session. Aborting anonymous auth.");
      return navigateToPath(PATHS.RATE_LIMIT);
    }

    const { sg: socialGifting } = getAllSearchParams();

    const isSocialGifting = socialGifting === "true";
    if (isExpired({ isBase64: !isSocialGifting })) {
      return navigateToPath(PATHS.LINK_EXPIRED);
    }

    const anonymousAuthData = this.buildAnonumousAuthData();

    try {
      const { process_key: processKey } = await campaignApi.anonymousStart(anonymousAuthData);
      await withRetry({fn: () => this.anonymousAuth({ processKey, fingerprint: anonymousAuthData.fingerprint })});
    } catch (error) {
      await AnonymousAuthErrorResponseHandler.handleErrorResponse(error);
    }

    if (anonymousAuthData.social_gifting) {
      await handleSocialGifting(anonymousAuthData);
    }
  }
}
